import React from 'react';
import PropTypes from 'prop-types';
import Joyride, {ACTIONS, EVENTS, STATUS} from 'react-joyride';
import CustomTooltip from './custom-tooltip.jsx';

const tourStyles = {
    options: {
        arrowColor: '#ffffff',
        primaryColor: '#FF9D3B',
        zIndex: 10000
    }
};

// Index of the "Board Selection" step (0-indexed).
// After this step, we pause the tour, open the device library,
// show the board-highlight step, then close and resume.
const BOARD_SELECTION_INDEX = 3;
const EXTENSION_SELECTION_INDEX = 9;
const SPRITE_SELECTION_INDEX = 12;
const BACKDROP_SELECTION_INDEX = 13;

const mainSteps = [
    {
        target: 'body',
        title: 'Welcome',
        content: 'Welcome to the TIF Studio! Let us take a quick tour of the main areas.',
        placement: 'center',
        disableBeacon: true
    },
    {
        target: '#app-launcher-root',
        title: 'App Launcher',
        content: 'Access other TIF applications and games from here.',
        placement: 'right',
        spotlightPadding: 2
    },
    {
        target: '[title="Change Language"]',
        title: 'Language',
        content: 'Click here to change the language of the studio.',
        spotlightPadding: 2
    },
    {
        target: '#tour-device-selector',
        title: 'Board Selection',
        content: 'Click here to choose which hardware board you want to use. ' +
            "Let's open it and take a look!",
        spotlightPadding: 2
    },
    {
        target: '#tour-device-connection',
        title: 'Device Connection',
        content: 'Then, click here to connect your selected board to the studio.',
        spotlightPadding: 2
    },
    {
        target: '[title="Download Agent"]',
        title: 'Download Agent',
        content: 'Click here to download the TIF Agent for your device.',
        spotlightPadding: 2
    },
    {
        target: '#tour-tabs-target',
        title: 'Editor Tabs',
        content: 'Switch between your blocks Code, character Costumes, and Sounds.',
        spotlightPadding: 0
    },
    {
        target: '#tour-palette-only',
        title: 'Blocks Palette',
        content: 'This is the Blocks Palette. Drag blocks from here to build your code.',
        spotlightPadding: 0
    },
    {
        target: '#tour-workspace-only',
        title: 'Workspace',
        content: 'Drop your blocks here to create scripts. ' +
            'To delete blocks, just drag them back to the palette.',
        spotlightPadding: 0
    },
    {
        target: '#tour-extension-button',
        title: 'Extensions',
        content: 'Add extra capabilities like AI and other Scratch-related ' +
            'blocks using the Extension Library.',
        spotlightPadding: 5
    },
    {
        target: '#tour-stage-area',
        title: 'Stage',
        content: 'This is where you will see the visual result of your Scratch programs running.'
    },
    {
        target: '#tour-sprite-area',
        title: 'Sprite Area',
        content: 'Manage your characters and backgrounds here.'
    },
    {
        target: '[class*="sprite-selector_add-button"] button',
        title: 'Choose a Sprite',
        content: 'Click here to choose a new sprite for your project.',
        spotlightPadding: 2
    },
    {
        target: '[class*="stage-selector_add-button"] button',
        title: 'Choose a Backdrop',
        content: 'Click here to choose a new backdrop for your stage.',
        spotlightPadding: 2
    },
    {
        target: '#tour-mode-switch',
        title: 'Program Mode',
        content: 'Finally, switch to Program mode so you can upload your code directly to the board!',
        spotlightPadding: 2
    },
    {
        target: '#tour-tabs-target',
        title: 'Program Libraries & Boards',
        content: 'In Program Mode, you can access the Code editor, Extension Libraries, and Boards Manager here.',
        spotlightPadding: 0
    },
    {
        target: '[class*="hardware_code-editor-wrapper"]',
        title: 'Code Area',
        content: 'This is where you can view or write the actual code (C/C++, Python) for your board.',
        spotlightPadding: 5
    },
    {
        target: '[class*="hardware_hardware-console-wrapper"]',
        title: 'Serial Monitor',
        content: 'View the serial output from your connected board here.',
        spotlightPadding: 5
    },
    {
        target: '#tour-extension-button',
        title: 'Extensions (Program Mode)',
        content: 'You can still add extensions here while in Program mode to get new blocks!',
        spotlightPadding: 5
    },
    {
        target: 'body',
        title: 'Ready to Create!',
        content: 'You\'re all set! Go build something amazing.',
        placement: 'center',
        spotlightPadding: 0
    }
];

// eslint-disable-next-line max-len
const BOARD_LIBRARY_STEP_TARGET = '[class*="library-scroll-grid"] > div:nth-child(2)';

const boardLibraryStep = {
    target: BOARD_LIBRARY_STEP_TARGET,
    title: 'Choose a Board',
    content: 'Here you can see all the available boards. ' +
        'Select one to start programming it!',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
    disableScroll: true
};

const connectionModalStep = {
    target: '[class*="connection-modal_body"]',
    title: 'Connect Your Device',
    content: 'Here you can see the connection status and connect or disconnect your device.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
    disableScroll: true
};

const extensionLibraryStep = {
    target: '[class*="library-scroll-grid"] [class*="library-item"]:first-child',
    title: 'Choose an Extension',
    content: 'Here you can see all the available extensions. Select one to add extra blocks!',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
    disableScroll: true
};

const spriteLibraryStep = {
    target: '[class*="library-scroll-grid"] [class*="library-item"]:first-child',
    title: 'Choose a Sprite',
    content: 'Here you can see all the available sprites. Select one to add to your project!',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
    disableScroll: true
};

const backdropLibraryStep = {
    target: '[class*="library-scroll-grid"] [class*="library-item"]:first-child',
    title: 'Choose a Backdrop',
    content: 'Here you can see all the available backdrops. Select one to change your stage background!',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
    disableScroll: true
};

class Tour extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            stepIndex: 0,
            phase: 'main' // 'main' | 'board-library' | 'extension-library' | 'sprite-library' | 'backdrop-library'
        };
        this.handleJoyrideCallback = this.handleJoyrideCallback.bind(this);
        // eslint-disable-next-line max-len
        this.handleBoardLibraryCallback = this.handleBoardLibraryCallback.bind(this);
        this.handleConnectionModalCallback = this.handleConnectionModalCallback.bind(this);
        // eslint-disable-next-line max-len
        this.handleExtensionLibraryCallback = this.handleExtensionLibraryCallback.bind(this);
        this.handleSpriteLibraryCallback = this.handleSpriteLibraryCallback.bind(this);
        this.handleBackdropLibraryCallback = this.handleBackdropLibraryCallback.bind(this);
        this.finishTour = this.finishTour.bind(this);
    }

    componentDidUpdate (prevProps) {
        if (this.props.run && !prevProps.run) {
            // Reset state when the tour is restarted
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                stepIndex: 0,
                phase: 'main'
            });
        }
    }

    finishTour () {
        const isProgramActive = document.querySelector('#tour-mode-switch [class*="is-program"][class*="active"]');
        if (isProgramActive) {
            const modeSwitch = document.querySelector('#tour-mode-switch');
            if (modeSwitch) modeSwitch.click();
        }
        if (this.props.onFinish) {
            this.props.onFinish();
        }
    }

    handleJoyrideCallback (data) {
        const {action, index, type, status} = data;

        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
        if (finishedStatuses.includes(status)) {
            this.finishTour();
            return;
        }

        if (type === EVENTS.TOOLTIP) {
            if (index === 1) {
                const appLauncherBtn = document.querySelector('#app-launcher-root button');
                if (appLauncherBtn && !appLauncherBtn.className.includes('active')) {
                    appLauncherBtn.click();
                }
            } else if (index === 2) {
                const langSelect = document.querySelector('[title="Change Language"] select');
                if (langSelect) {
                    langSelect.size = langSelect.options.length;
                    langSelect.style.position = 'absolute';
                    langSelect.style.zIndex = '10001';
                }
            } else if (index === 14) {
                const isProgramActive = document.querySelector('#tour-mode-switch [class*="is-program"][class*="active"]');
                if (!isProgramActive) {
                    const modeSwitch = document.querySelector('#tour-mode-switch');
                    if (modeSwitch) modeSwitch.click();
                }
            } else if (index === 19) {
                const isProgramActive = document.querySelector('#tour-mode-switch [class*="is-program"][class*="active"]');
                if (isProgramActive) {
                    const modeSwitch = document.querySelector('#tour-mode-switch');
                    if (modeSwitch) modeSwitch.click();
                }
            }
        }

        if (type === EVENTS.STEP_AFTER) {
            if (index === 1) {
                const appLauncherBtn = document.querySelector('#app-launcher-root button');
                if (appLauncherBtn && appLauncherBtn.className.includes('active')) {
                    appLauncherBtn.click();
                }
            }

            if (index === 2) {
                const langSelect = document.querySelector('[title="Change Language"] select');
                if (langSelect) {
                    langSelect.size = 0;
                    langSelect.style.position = '';
                    langSelect.style.zIndex = '';
                }
            }

            if (index === BOARD_SELECTION_INDEX) {
                // User clicked "Next" on the board selection step.
                // Pause the main tour, open the device library.
                if (this.props.onOpenDeviceLibrary) {
                    this.props.onOpenDeviceLibrary();
                }
                // Wait for the modal to render, then show
                // the board-library Joyride.
                setTimeout(() => {
                    this.setState({phase: 'board-library'});
                }, 800);
                return;
            }



            if (index === EXTENSION_SELECTION_INDEX || index === 18) {
                // User clicked "Next" on the extension selection step.
                // Pause the main tour, open the extension library.
                if (this.props.onOpenExtensionLibrary) {
                    this.props.onOpenExtensionLibrary();
                }
                // Wait for the modal to render, then show
                // the extension-library Joyride.
                setTimeout(() => {
                    this.setState({phase: 'extension-library'});
                }, 800);
                return;
            }

            if (index === SPRITE_SELECTION_INDEX) {
                const addSpriteBtn = document.querySelector('[class*="sprite-selector_add-button"] button');
                if (addSpriteBtn) addSpriteBtn.click();
                setTimeout(() => {
                    this.setState({phase: 'sprite-library'});
                }, 800);
                return;
            }

            if (index === BACKDROP_SELECTION_INDEX) {
                const addBackdropBtn = document.querySelector('[class*="stage-selector_add-button"] button');
                if (addBackdropBtn) addBackdropBtn.click();
                setTimeout(() => {
                    this.setState({phase: 'backdrop-library'});
                }, 800);
                return;
            }

            // Normal forward navigation
            if (action === ACTIONS.NEXT) {
                this.setState({stepIndex: index + 1});
            }
        }

        if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
            this.setState({stepIndex: index - 1});
        }
    }

    handleConnectionModalCallback (data) {
        const {action, type, status} = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
        if (
            finishedStatuses.includes(status) ||
            type === EVENTS.STEP_AFTER
        ) {
            // Close the connection modal
            const closeBtn = document.querySelector('[class*="connection-modal"] [class*="close-button_close-button"]');
            if (closeBtn) closeBtn.click();
            
            // Resume the main tour at step 4 (the Device Connection button)
            this.setState({
                phase: 'main',
                stepIndex: 4
            });
            return;
        }

        if (action === ACTIONS.SKIP) {
            const closeBtn = document.querySelector('[class*="connection-modal"] [class*="close-button_close-button"]');
            if (closeBtn) closeBtn.click();
            this.finishTour();
        }
    }

    handleBoardLibraryCallback (data) {
        const {action, type, status} = data;

        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
        if (
            finishedStatuses.includes(status) ||
            type === EVENTS.STEP_AFTER
        ) {
            // Select the second board
            const secondBoard = document.querySelector('[class*="library-scroll-grid"] > div:nth-child(2)');
            if (secondBoard) secondBoard.click();

            // After selecting the board, immediately open the connection modal and start its tour phase
            setTimeout(() => {
                const deviceConnectionBtn = document.querySelector('#tour-device-connection');
                if (deviceConnectionBtn) {
                    const event = new MouseEvent('mouseup', { bubbles: true, cancelable: true });
                    deviceConnectionBtn.dispatchEvent(event);
                }
                
                this.setState({phase: 'connection-modal'});
            }, 800);
            return;
        }

        if (action === ACTIONS.SKIP) {
            if (this.props.onCloseDeviceLibrary) {
                this.props.onCloseDeviceLibrary();
            }
            this.finishTour();
        }
    }

    handleExtensionLibraryCallback (data) {
        const {action, type, status} = data;

        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
        if (
            finishedStatuses.includes(status) ||
            type === EVENTS.STEP_AFTER
        ) {
            // Close the extension library and resume main tour
            if (this.props.onCloseExtensionLibrary) {
                this.props.onCloseExtensionLibrary();
            }
            // Resume the main tour at the step AFTER extension selection
            const nextStep = this.state.stepIndex + 1;
            if (nextStep >= mainSteps.length) {
                this.finishTour();
                return;
            }
            this.setState({
                phase: 'main',
                stepIndex: nextStep
            });
            return;
        }

        if (action === ACTIONS.SKIP) {
            if (this.props.onCloseExtensionLibrary) {
                this.props.onCloseExtensionLibrary();
            }
            this.finishTour();
        }
    }

    handleSpriteLibraryCallback (data) {
        const {action, type, status} = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
        if (finishedStatuses.includes(status) || type === EVENTS.STEP_AFTER) {
            const closeBtn = document.querySelector('[class*="modal_back-button"]');
            if (closeBtn) closeBtn.click();
            this.setState({
                phase: 'main',
                stepIndex: SPRITE_SELECTION_INDEX + 1
            });
            return;
        }
        if (action === ACTIONS.SKIP) {
            const closeBtn = document.querySelector('[class*="modal_back-button"]');
            if (closeBtn) closeBtn.click();
            this.finishTour();
        }
    }

    handleBackdropLibraryCallback (data) {
        const {action, type, status} = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
        if (finishedStatuses.includes(status) || type === EVENTS.STEP_AFTER) {
            const closeBtn = document.querySelector('[class*="modal_back-button"]');
            if (closeBtn) closeBtn.click();
            this.setState({
                phase: 'main',
                stepIndex: BACKDROP_SELECTION_INDEX + 1
            });
            return;
        }
        if (action === ACTIONS.SKIP) {
            const closeBtn = document.querySelector('[class*="modal_back-button"]');
            if (closeBtn) closeBtn.click();
            this.finishTour();
        }
    }

    render () {
        const {phase, stepIndex} = this.state;
        const isMain = phase === 'main';
        const isBoardLibrary = phase === 'board-library';
        const isConnectionModal = phase === 'connection-modal';
        const isExtensionLibrary = phase === 'extension-library';
        const isSpriteLibrary = phase === 'sprite-library';
        const isBackdropLibrary = phase === 'backdrop-library';

        return (
            <React.Fragment>
                <Joyride
                    continuous
                    scrollToFirstStep
                    showProgress
                    showSkipButton
                    // eslint-disable-next-line react/jsx-handler-names
                    callback={this.handleJoyrideCallback}
                    run={this.props.run && isMain}
                    stepIndex={stepIndex}
                    steps={mainSteps}
                    styles={tourStyles}
                    tooltipComponent={CustomTooltip}
                />
                {isBoardLibrary && (
                    <Joyride
                        showSkipButton
                        // eslint-disable-next-line react/jsx-handler-names
                        callback={this.handleBoardLibraryCallback}
                        run={this.props.run}
                        steps={[boardLibraryStep]}
                        styles={tourStyles}
                        tooltipComponent={CustomTooltip}
                        disableScrolling={true}
                    />
                )}
                {isConnectionModal && (
                    <Joyride
                        showSkipButton
                        // eslint-disable-next-line react/jsx-handler-names
                        callback={this.handleConnectionModalCallback}
                        run={this.props.run}
                        steps={[connectionModalStep]}
                        styles={tourStyles}
                        tooltipComponent={CustomTooltip}
                        disableScrolling={true}
                    />
                )}
                {isExtensionLibrary && (
                    <Joyride
                        showSkipButton
                        // eslint-disable-next-line react/jsx-handler-names
                        callback={this.handleExtensionLibraryCallback}
                        run={this.props.run}
                        steps={[extensionLibraryStep]}
                        styles={tourStyles}
                        tooltipComponent={CustomTooltip}
                        disableScrolling={true}
                    />
                )}
                {isSpriteLibrary && (
                    <Joyride
                        showSkipButton
                        // eslint-disable-next-line react/jsx-handler-names
                        callback={this.handleSpriteLibraryCallback}
                        run={this.props.run}
                        steps={[spriteLibraryStep]}
                        styles={tourStyles}
                        tooltipComponent={CustomTooltip}
                        disableScrolling={true}
                    />
                )}
                {isBackdropLibrary && (
                    <Joyride
                        showSkipButton
                        // eslint-disable-next-line react/jsx-handler-names
                        callback={this.handleBackdropLibraryCallback}
                        run={this.props.run}
                        steps={[backdropLibraryStep]}
                        styles={tourStyles}
                        tooltipComponent={CustomTooltip}
                        disableScrolling={true}
                    />
                )}
            </React.Fragment>
        );
    }
}

Tour.propTypes = {
    onCloseDeviceLibrary: PropTypes.func,
    onCloseExtensionLibrary: PropTypes.func,
    onFinish: PropTypes.func,
    onOpenDeviceLibrary: PropTypes.func,
    onOpenExtensionLibrary: PropTypes.func,
    run: PropTypes.bool.isRequired
};

export default Tour;
